import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';

import { formatClientDate, safeParseISO } from 'shared/util/datetime-fns';
import type { PaleState } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const originAirports: Record<string, string> = displayValues.originAirports;

export const HotelFilterPreview = () => {
    const { origin, departureDate, returnDate, travelMonths, duration, ignoredFilters, numberOfAdults, agesOfChildren } = useSelector(
        (state: PaleState) => state.filters,
    );
    const originDisplay = originAirports[origin];
    const parsedDepartureDate = safeParseISO(departureDate?.date);
    const parsedReturnDate = safeParseISO(returnDate?.date);

    const dateInfo = (): ReactElement => {
        if (parsedDepartureDate && parsedReturnDate && !ignoredFilters.includes('departureDate')) {
            return (
                <div data-testid="hotelFilterPreview-dateInfo-fixed">
                    <span className="whitespace-nowrap">{formatClientDate(parsedDepartureDate)}</span>
                    <span> - </span>
                    <span className="whitespace-nowrap">{formatClientDate(parsedReturnDate)}</span>
                </div>
            );
        }
        if (travelMonths.length !== 0 || (duration && !ignoredFilters.includes('travelMonths'))) {
            return (
                <div data-testid="hotelFilterPreview-dateInfo-flex">
                    <span className="font-bold">{duration ? `${duration[0]} - ${duration[1]} dager` : 'Når som helst'}</span>
                    {travelMonths.length !== 0 && travelMonths.length !== 12 ? ' i utvalgte måneder' : ''}
                </div>
            );
        }
        return <div data-testid="hotelFilterPreview-dateInfo-default">Når som helst</div>;
    };

    const paxText = `${numberOfAdults} ${numberOfAdults === 1 ? 'voksen' : 'voksne'}${agesOfChildren && agesOfChildren.length > 0 ? `, ${agesOfChildren?.length} barn` : ''}`;

    return (
        <div className="s-divide grow flex flex-col md:flex-row gap-8 md:gap-1 md:text-m" data-testid="hotelFilterPreview">
            <div className="flex flex-col justify-center">
                <div className="md:pr-24 flex gap-8 justify-items-center items-center">
                    <w-icon name="PlaneTakeOff" size="small" locale="nb" aria-hidden></w-icon>
                    <span data-testid="hotelFilterPreview-origin">{originDisplay}</span>
                </div>
            </div>
            <div className="flex flex-col justify-center md:border-l-1">
                <div className="text-center md:px-24 flex gap-8 justify-items-center items-center">
                    <w-icon name="Calendar" size="small" locale="nb" aria-hidden></w-icon>
                    {dateInfo()}
                </div>
            </div>
            <div className="flex-col justify-center hidden md:border-l-1 md:flex">
                <div className="text-center md:px-24 px-8 flex gap-8 justify-items-center items-center">
                    <w-icon name="Users" size="small" locale="nb" aria-hidden></w-icon>
                    <span className="bottom-a align-text-bottom object-bottom">{paxText}</span>
                </div>
            </div>
        </div>
    );
};
